import type { ColumnDef } from "@tanstack/react-table";
import type { Product } from "@/types";
import { Badge } from "@trillion/ui/components/ui/badge";
import { createActionsColumn } from "@trillion/ui";
import { formatPrice } from "@trillion/ui/lib/format-price";

interface ProductColumnsOptions {
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
}

export function getProductColumns({ onEdit, onDelete }: ProductColumnsOptions): ColumnDef<Product>[] {
  return [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="max-w-[200px] truncate font-medium">{row.getValue("name")}</div>
      ),
    },
    {
      accessorKey: "brand_name",
      header: "Brand",
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.getValue("brand_name") || "-"}</span>
      ),
    },
    {
      accessorKey: "country_code",
      header: "Country",
      cell: ({ row }) => <span className="font-mono text-sm">{row.getValue("country_code")}</span>,
    },
    {
      accessorKey: "catalog_id",
      header: "Catalog",
      cell: ({ row }) => (
        <span className="font-mono text-sm text-muted-foreground">
          {row.getValue("catalog_id") || "-"}
        </span>
      ),
    },
    {
      accessorKey: "parent_sku",
      header: "Parent SKU",
      cell: ({ row }) => (
        <span className="font-mono text-sm text-muted-foreground">
          {row.getValue("parent_sku") || "-"}
        </span>
      ),
    },
    {
      accessorKey: "item_group_id",
      header: "Item Group",
      cell: ({ row }) => (
        <span className="font-mono text-sm text-muted-foreground">
          {row.getValue("item_group_id") || "-"}
        </span>
      ),
    },
    {
      accessorKey: "price",
      header: "Price",
      cell: ({ row }) => {
        // Currency-aware (JPY renders ¥, GBP £, …) — never a hardcoded $.
        const formatted = formatPrice(row.original.price, row.original.currency ?? undefined);
        return <span>{formatted || "-"}</span>;
      },
    },
    {
      accessorKey: "active",
      header: "Status",
      cell: ({ row }) => {
        const active = row.getValue("active") as boolean;
        return (
          <Badge variant={active ? "default" : "destructive"}>
            {active ? "Active" : "Inactive"}
          </Badge>
        );
      },
    },
    {
      accessorKey: "in_stock",
      header: "In Stock",
      cell: ({ row }) => {
        const inStock = row.getValue("in_stock") as boolean;
        return (
          <Badge variant={inStock ? "default" : "destructive"}>
            {inStock ? "In Stock" : "Out of Stock"}
          </Badge>
        );
      },
    },
    createActionsColumn<Product>({ onEdit, onDelete }),
  ];
}
